import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { estimatedTotalNote, feesUnverifiedNote, inheritedExtras, firstYearOnly, FIRST_YEAR_TOTAL_LABEL } from "@/lib/feeYear";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { pushPendingShortlist } from "@/lib/pendingIntent";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import FeeVsSalaryCard from "@/components/app/degree/FeeVsSalaryCard";
import ApplyReadinessCard from "@/components/ApplyReadinessCard";
import DataHonestyBadges from "@/components/app/DataHonestyBadges";
import LimitedDataBanner from "@/components/app/degree/LimitedDataBanner";
import DegreeSEO from "@/components/app/degree/DegreeSEO";
import ISEECalcChip from "@/components/app/degree/ISEECalcChip";
import CrossCountryCostCompare from "@/components/app/degree/CrossCountryCostCompare";
import VariantPicker from "@/components/app/degree/VariantPicker";
import CopyPairedUcasButton from "@/components/app/degree/CopyPairedUcasButton";
import UsEarningsComparison from "@/components/app/degree/UsEarningsComparison";
import TotalCourseAndLivingCost from "@/components/app/degree/TotalCourseAndLivingCost";
import MoneyLayerCard from "@/components/app/degree/MoneyLayerCard";
import YourFeeHereTile from "@/components/app/YourFeeHereTile";
import FranceEssentials from "@/components/app/FranceEssentials";
import StudentVoiceTab from "@/components/app/StudentVoiceTab";
import StudentVoiceQuote from "@/components/app/StudentVoiceQuote";
import LifeAtUniBento from "@/components/app/LifeAtUniBento";
import { Landmark } from "lucide-react";
import { ClusterProvider, ClusterHeader, ClusterMember } from "@/components/app/degree/ClusterLayout";
import TextSizeToggle, { loadTextSize } from "@/components/app/degree/TextSizeToggle";
import {
  DegreeBreadcrumb,
  CityGuideLink,
  UniLifeLink,
  SimilarCoursesLink,
} from "@/components/app/degree/DegreeInterlinks";
import CourseTipsCard from "@/components/app/degree/CourseTipsCard";
import EmptyCellHelpLink from "@/components/app/EmptyCellHelpLink";
import VerifiedCoverageChip from "@/components/app/degree/VerifiedCoverageChip";
import NssSubjectCard from "@/components/app/NssSubjectCard";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import DiscoverUniPanel from "@/components/app/DiscoverUniPanel";
import DiscoverUniHeroStrip from "@/components/app/DiscoverUniHeroStrip";
import LEOSalaryStrip from "@/components/app/LEOSalaryStrip";
import SalaryProjectionChart from "@/components/app/SalaryProjectionChart";
import VisaSponsorVerifiedChip from "@/components/app/VisaSponsorVerifiedChip";
import EntryProfileMini from "@/components/app/EntryProfileMini";
import SalaryProgressionWidget from "@/components/app/SalaryProgressionWidget";
import SatisfactionCard from "@/components/app/SatisfactionCard";
import WillYouEnjoyItCard from "@/components/app/degree/WillYouEnjoyItCard";
import StudentLovedBadge from "@/components/app/StudentLovedBadge";
import CostGapCard from "@/components/app/CostGapCard";
import ContinentalAlternativesCard from "@/components/app/ContinentalAlternativesCard";
import { CAODeadlineChip, CAOChangeOfMindBanner } from "@/components/app/CAODeadline";
import { UCASDeadlineChip } from "@/components/app/UCASDeadlineChip";
import { PTDeadlineChip } from "@/components/app/PTDeadlineChip";
import PlacementsDeepDiveCard from "@/components/app/PlacementsDeepDiveCard";
import EvidenceBanner from "@/components/app/EvidenceBanner";
import VerifiedByUniBadge from "@/components/app/VerifiedByUniBadge";
import FoundationYearPill from "@/components/app/FoundationYearPill";
import WhatYouStudyCard from "@/components/app/WhatYouStudyCard";
import DifferentiatorsGrid from "@/components/app/DifferentiatorsGrid";
import KeyFacultyCard from "@/components/app/KeyFacultyCard";
import {
  useAIInterpretation,
  AIInterpretationSummary,
  AIInterpretationNote,
} from "@/components/app/AIInterpretationCard";
import { useStudentLovedBulk } from "@/lib/useStudentLoved";
import { resolveOfferRate, offerRateChipClasses, offerRateChipLabel, offerRateMetricLabel } from "@/lib/offerRate";
import { AlertTriangle, CheckCircle2, ExternalLink, ArrowLeft, Bookmark, Check, GitCompare, Copy, ShieldCheck, Briefcase } from "lucide-react";
import FirmLogo from "@/components/app/FirmLogo";
import { Ev, SectionNav, Section, DataRow, DegreePageProvider } from "@/components/app/degree/DegreeDetailBits";
import VerifyMoreNudge from "@/components/app/VerifyMoreNudge";
import ClaimDiscoveryCTA from "@/components/app/ClaimDiscoveryCTA";
import { ScopeChip, OutcomeScopeLegend } from "@/components/app/degree/ScopeChip";
import {
  TefBadge,
  HesaAccreditations,
  TopGraduateJobs,
  LeoComparison,
  UcasPathways,
} from "@/components/app/degree/HesaPhase1Bits";
import {
  NssThemesBlock,
  EmploymentBreakdownBlock,
  LocationBlock,
  CourseMetaChips,
} from "@/components/app/degree/HesaPhase3Bits";
import LeoSectorChart from "@/components/app/degree/LeoSectorChart";
import HesaEntryProfile from "@/components/app/degree/HesaEntryProfile";
import AcademicFitPanel from "@/components/app/degree/AcademicFitPanel";
import { pickAcademicSignals, primaryDisplayValue } from "@/lib/academic-signals";
import DaadEntryProfile from "@/components/app/degree/DaadEntryProfile";
import DeSubjectBaseline from "@/components/app/degree/DeSubjectBaseline";
import UsEntryProfile from "@/components/app/degree/UsEntryProfile";
import UsFinancialAid from "@/components/app/degree/UsFinancialAid";
import { UsInstitutionSignals, UsSubjectBaseline } from "@/components/app/degree/UsInstitutionSignals";
import HesaAdmissionsSnapshot from "@/components/app/degree/HesaAdmissionsSnapshot";
import { SmallSample } from "@/components/app/degree/HesaAdmissionsSnapshot";
import HesaCurriculumSnapshot from "@/components/app/degree/HesaCurriculumSnapshot";
import TeachingLearningCluster from "@/components/app/degree/TeachingLearningCluster";
import { EntryTariffKisCard } from "@/components/app/degree/EntryTariffKisCard";
import { useShortlistWriter } from "@/lib/useShortlistWriter";
import EventCard from "@/components/app/EventCard";
import DegreeEventsInline from "@/components/app/degree/DegreeEventsInline";
import PostgradSiblingsWidget from "@/components/app/degree/PostgradSiblingsWidget";
import DegreeSourcesPanel from "@/components/app/DegreeSourcesPanel";
import { filterScholarshipsByLevel } from "@/utils/scholarshipLevel";
import Cluster1Essential from "@/pages/degree-clusters/Cluster1Essential";
import Cluster2Applying  from "@/pages/degree-clusters/Cluster2Applying";
import Cluster3Costs     from "@/pages/degree-clusters/Cluster3Costs";
import Cluster4ROI       from "@/pages/degree-clusters/Cluster4ROI";
import Cluster5Enjoy     from "@/pages/degree-clusters/Cluster5Enjoy";
import Cluster6Sources   from "@/pages/degree-clusters/Cluster6Sources";


// ---------------------------------------------------------------------------
// Country-aware Academic Fit
// ---------------------------------------------------------------------------
// Different destinations sell their offers in different grade currencies.
// Each mapping is a *priority list* of keys we'll try — if the primary
// (e.g. SAT) isn't populated we fall through to the next native option
// (e.g. GPA via `american_diploma`). We deliberately do NOT fall back to
// A-Level outside the UK/IE family — showing "A*A*A" as the primary for
// a Princeton degree misrepresents the actual ask.
const COUNTRY_ACADEMIC_MAP = {
  GB: { keys: ["a_level"],                              label: "A-Level (typical)" },
  IE: { keys: ["a_level"],                              label: "Leaving Cert (typical)" },
  US: { keys: ["sat", "gpa", "american_diploma"],       label: "SAT / GPA (typical)" },
  AU: { keys: ["atar"],                                 label: "ATAR (typical)" },
  DE: { keys: ["abitur"],                               label: "Abitur (typical)" },
  FR: { keys: ["bac", "french_bac"],                    label: "Baccalauréat (typical)" },
  NL: { keys: ["vwo"],                                  label: "VWO (typical)" },
  IT: { keys: ["maturita"],                             label: "Maturità (typical)" },
};

const _COUNTRY_NAME_TO_CODE = {
  "united kingdom": "GB", "uk": "GB", "england": "GB", "scotland": "GB",
  "wales": "GB", "northern ireland": "GB",
  "united states": "US", "usa": "US", "us": "US", "america": "US",
  "australia": "AU", "germany": "DE", "france": "FR", "ireland": "IE",
  "netherlands": "NL", "italy": "IT",
};

function _countryCode(uni) {
  if (uni?.country_code) return uni.country_code.toUpperCase();
  if (uni?.country) {
    const key = String(uni.country).trim().toLowerCase();
    if (_COUNTRY_NAME_TO_CODE[key]) return _COUNTRY_NAME_TO_CODE[key];
  }
  return null;
}

function _pickTypical(req, key) {
  const node = req?.[key];
  if (!node) return null;
  if (typeof node === "string") return { value: node, estimated: false, source: null };
  if (node.typical != null) {
    return {
      value: String(node.typical),
      estimated: node.estimated === true,
      source: node.source || null,
    };
  }
  if (node.minimum != null) {
    return {
      value: String(node.minimum),
      estimated: node.estimated === true,
      source: node.source || null,
    };
  }
  return null;
}

// Guard: `_pickTypical` used to return a bare string. Anywhere that
// still wants the string can wrap with this. Otherwise callers should
// read `.value` / `.estimated` off the object shape above.
function _pickTypicalStr(req, key) {
  const p = _pickTypical(req, key);
  return p ? p.value : null;
}

export function pickAcademicFit(uni, entry_requirements = {}, hs = {}, admissions = {}) {
  const code = _countryCode(uni);
  const mapping = code ? COUNTRY_ACADEMIC_MAP[code] : null;

  // Try the country's native systems in priority order first. Only fall
  // back to A-Level / UCAS points when the destination is UK/IE — we
  // don't want to headline "A*A*A" on a Princeton or ANU page just
  // because the sibling field is present for international overlays.
  const ukFamily = code === "GB" || code === "IE" || !code;

  // AUDITION / PORTFOLIO gate override — for conservatoires and
  // performance courses we've explicitly flagged, we always show the
  // uni's own published tariff (or "Audition only") instead of the
  // HESA cohort figure. HESA reports what admitted students *had*,
  // not what the uni *requires* — for these courses those diverge
  // by 2+ A-level grades. Extends to US portfolio schools where a
  // reported ~19% acceptance is entirely portfolio-driven.
  if (admissions?.entry_gate === "audition") {
    const t = admissions?.published_ucas_tariff;
    if (t === 0 || t == null) {
      return {
        primary: { system: "Entry gate", value: "Audition-only" },
        entry_gate: "audition",
      };
    }
    return {
      primary: { system: "Published tariff floor",
                 value: `${t} pts · audition-led` },
      entry_gate: "audition",
    };
  }
  if (admissions?.entry_gate === "portfolio") {
    const t = admissions?.published_ucas_tariff;
    if (t === 0 || t == null) {
      return {
        primary: { system: "Entry gate", value: "Portfolio-only" },
        entry_gate: "portfolio",
      };
    }
    return {
      primary: { system: "Published tariff floor",
                 value: `${t} pts · portfolio-led` },
      entry_gate: "portfolio",
    };
  }

  // For UK/IE, the advertised UCAS tariff (standard/contextual) is
  // more accurate than a_level.typical — HESA fills a_level with "Pass"
  // as a fallback whenever the real offer is in tariff points.
  if (ukFamily) {
    const tariff = entry_requirements?.ucas_tariff;
    if (tariff?.standard != null) {
      const label = tariff.contextual != null
        ? `${tariff.standard} pts (contextual ${tariff.contextual})`
        : `${tariff.standard} pts`;
      return { primary: { system: "UCAS tariff (standard offer)", value: label } };
    }
  }

  if (mapping) {
    for (const key of mapping.keys) {
      const p = _pickTypical(entry_requirements, key);
      // "Pass" is a HESA sentinel — skip it so we fall through to a
      // more useful signal (tariff median, native curriculum, etc.).
      if (p && String(p.value).trim().toLowerCase() !== "pass") {
        return {
          primary: {
            system: mapping.label,
            value: p.value,
            estimated: p.estimated,
            source: p.source,
          },
        };
      }
    }
  }
  if (ukFamily) {
    const v = _pickTypicalStr(entry_requirements, "a_level") || hs?.typical_a_level;
    const isMeaningful = v && String(v).trim().toLowerCase() !== "pass";
    if (isMeaningful) return { primary: { system: "A-Level (typical)", value: v } };
    if (hs?.typical_tariff_points) {
      return { primary: { system: "UCAS tariff (median intake)", value: `${hs.typical_tariff_points} pts` } };
    }
    // Last-resort fallback: uni-wide published band. This kicks in
    // after the 2026-02-28 purge that unset stale seed
    // `entry_requirements.a_level` on rows where the seed value was
    // lower than the uni's OWN published minimum (Imperial Computing,
    // Cambridge Medicine et al. were the affected cases). The uni-
    // wide typical is closer to the truth than a phantom BBB.
    const uniAL = uni?.curricula?.a_level;
    if (uniAL?.typical && String(uniAL.typical).toLowerCase() !== "pass") {
      return {
        primary: {
          system: "A-Level (uni-wide typical)",
          value: uniAL.typical,
        },
      };
    }
  }
  return { primary: null };
}


function TuitionBasisNote({ intl, blocked }) {
  const lines = [
    blocked ? "Course tuition: not yet verified." : null,
    intl?.basis_label,
    intl?.source_year_label ? `${intl.source_year_label}.` : null,
  ].filter(Boolean);
  if (!lines.length) return null;
  return (
    <p className="md:col-span-3 text-xs text-stone-500 leading-snug" data-testid="degree-tuition-basis-note">
      {lines.join(" ")}
    </p>
  );
}

export default function DegreeDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [uni, setUni] = useState(null);
  const [saved, setSaved] = useState(false);
  // Per-employer 3y→5y lift keyed by slug — populated once we know
  // the degree's country + top_employers list. Values look like
  // {employer, lift_pct, n_degrees, currency}. Missing keys just
  // render the plain chip without a lift badge, so the section
  // degrades gracefully when a firm isn't in the accelerator index.
  const [employerLifts, setEmployerLifts] = useState({});
  // Feb 19, 2026 — "Comfortable" text-size mode for parents / older
  // readers. Value drives the `data-text-size` attribute on the page
  // wrapper below, which CSS in index.css scales against. Persists via
  // localStorage (loadTextSize/saveTextSize inside TextSizeToggle).
  const [textSize, setTextSize] = useState(() => loadTextSize());
  const { format, currency } = useCurrency();
  // Native-currency hint: for UK degrees we always want to show £ next
  // to whatever the user has selected. Only render when the user is NOT
  // already viewing GBP (otherwise it would be a redundant duplicate).
  const gbpHint = (n) => {
    if (n == null || currency === "GBP") return null;
    const gbp = Math.round(n);
    return `£${gbp.toLocaleString()}`;
  };
  const { user, login } = useAuth();
  const { addToShortlist: writeShortlist, removeFromShortlist: dropShortlist } = useShortlistWriter();
  const satisfactionMap = useStudentLovedBulk(id ? [id] : []);
  const satisfaction = satisfactionMap[id];
  const ai = useAIInterpretation(id);

  // Fetch aggregated employer lift % once we know the degree's
  // country + top_employers list. The batch endpoint returns lifts
  // for ALL requested names in one round-trip so we don't paint
  // then flicker per-chip. Non-UK/US degrees are skipped (the
  // ranking dataset only covers those two countries).
  useEffect(() => {
    const emps = d?.outcomes?.top_employers;
    if (!emps || emps.length === 0) return;
    const isUk = typeof d.id === "string" && d.id.startsWith("uk-");
    const isUs = typeof d.id === "string" && d.id.startsWith("us-");
    if (!isUk && !isUs) return;
    const country = isUk ? "UK" : "US";
    api
      .get(`/employers/lifts?country=${country}&names=${encodeURIComponent(emps.join(","))}`)
      .then((r) => setEmployerLifts(r?.data?.lifts || {}))
      .catch(() => setEmployerLifts({}));
  }, [d?.id, d?.outcomes?.top_employers]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/degrees/${id}`);
        setD(data);
        const { data: u } = await api.get(`/universities/${data.university_id}`);
        setUni(u);
        if (user) {
          try {
            const { data: sl } = await api.get("/shortlist");
            setSaved((sl.items || []).some((it) => it.degree_id === data.id));
          } catch {}
        }
      } catch {}
    })();
  }, [id, user]);

  // Inject schema.org Course JSON-LD so Google shows rich course
  // cards for this page (and any 3rd-party scraper gets a defensible
  // integration point that must attribute WUA back). Only publishes
  // the safe subset — see routes/public_data.py for the moat split.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let tag;
    api.get(`/public/degrees/${id}/schema-org`).then(({ data }) => {
      if (cancelled || !data) return;
      tag = document.createElement("script");
      tag.type = "application/ld+json";
      tag.dataset.wuaSchema = "course";
      tag.text = JSON.stringify(data);
      document.head.appendChild(tag);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (tag && tag.parentNode) tag.parentNode.removeChild(tag);
      // also cleanup any leftover from a prior render
      document.head.querySelectorAll('script[data-wua-schema="course"]')
        .forEach((n) => n.remove());
    };
  }, [id]);

  // Shortlist toggle — adds the degree when the button is off and
  // removes it when it's already on. Optimistic-toggles the local
  // `saved` flag and rolls back if the network call fails so the
  // button never gets stuck in a half-state.
  const toggleShortlist = async () => {
    if (!d) return;
    if (!user) {
      pushPendingShortlist(d.university_id, d.official_title, d.id);
      toast.info("Sign in to save — we'll add this degree automatically.");
      login();
      return;
    }
    const wasSaved = saved;
    setSaved(!wasSaved);
    try {
      if (wasSaved) {
        await dropShortlist({ university_id: d.university_id, degree_id: d.id });
        toast(`${d.official_title} removed from your shortlist`);
      } else {
        await writeShortlist({ university_id: d.university_id, degree_id: d.id });
        toast.success(`${d.official_title} added to your shortlist`);
      }
    } catch {
      setSaved(wasSaved); // rollback on failure
      toast.error(wasSaved ? "Could not remove. Please try again." : "Could not save. Please try again.");
    }
  };

  if (!d) return null;

  // For UK HESA degrees tuition lives on the university (regulated Home
  // fee, per-uni International fee) — merge those in as a fallback so
  // every course inherits its parent uni's tuition. Degree-level values
  // still win when present.
  const cBase = d.cost || {};
  // 2027/28 outlook from the API: UK Home cap + Intl estimate win over stored figures.
  const homeUk = d.fee_outlook?.home_uk;
  const intlFo = d.fee_outlook?.intl;
  // A quarantined course figure (e.g. a cost-of-attendance total stored as tuition) is never shown.
  const courseTuitionBlocked = !!d.fee_outlook?.course_tuition_quarantined;
  // University-wide fallbacks and undated/Scorecard-year figures make the total indicative, never an exact current cost.
  const indicativeTotal = !!(intlFo?.university_level || intlFo?.source_year_label);
  const estTotalNote = estimatedTotalNote(intlFo, { indicative: indicativeTotal });
  const partialFeesNote = feesUnverifiedNote(intlFo);
  const UNI_WIDE_NOTE = "University-wide figure, not a quote for this course · not yet verified";
  const intlFromUniFallback = intlFo?.estimate_usd == null && intlFo?.published_usd == null
    && (courseTuitionBlocked || cBase.tuition_intl_annual_usd == null) && uni?.tuition_intl_annual_usd != null;
  const feeTileNote = [
    (intlFo?.university_level || intlFromUniFallback) ? UNI_WIDE_NOTE : null,
    intlFo?.source_year_label || null,
    intlFo?.source_academic_year ? `${intlFo.source_academic_year} fee (source year)` : null,
    intlFo?.published_subtotal_usd != null && intlFo?.is_estimate
      ? `${intlFo.estimate_year} tuition estimate · sourced ${intlFo.published_subtotal_year} tuition + required fees $${intlFo.published_subtotal_usd.toLocaleString()}`
      : null,
  ].filter(Boolean).join(" · ") || null;
  const homeOther = d.fee_outlook?.home_other;
  const homeFromUniFallback = !homeUk && !homeOther
    && (courseTuitionBlocked || cBase.tuition_domestic_annual_usd == null) && uni?.tuition_domestic_annual_usd != null;
  const homeTileIndicative = !homeUk && !!(homeFromUniFallback || homeOther?.university_level || homeOther?.source_year_label);
  const homeTileNote = homeUk ? null : [
    (homeFromUniFallback || homeOther?.university_level) ? UNI_WIDE_NOTE : null,
    homeOther?.source_year_label || null,
  ].filter(Boolean).join(" · ") || null;
  const c = {
    ...cBase,
    tuition_domestic_annual_usd:
      homeUk?.amount_usd ?? d.fee_outlook?.home_other?.estimate_usd ?? d.fee_outlook?.home_other?.published_usd
      ?? (courseTuitionBlocked ? null : cBase.tuition_domestic_annual_usd) ?? uni?.tuition_domestic_annual_usd ?? null,
    tuition_intl_annual_usd:
      intlFo?.estimate_usd ?? intlFo?.published_usd ?? (courseTuitionBlocked ? null : cBase.tuition_intl_annual_usd)
      ?? uni?.tuition_intl_annual_usd ?? null,
    tuition_intl_source_tier:
      cBase.tuition_intl_source_tier ?? uni?.tuition_intl_source_tier ?? null,
    tuition_domestic_annual_gbp:
      homeUk?.amount_gbp ?? cBase.tuition_domestic_annual_gbp ?? uni?.tuition_domestic_annual_gbp ?? null,
    tuition_intl_annual_gbp:
      cBase.tuition_intl_annual_gbp ?? uni?.tuition_intl_annual_gbp ?? null,
  };
  const hs = d.hesa_stats || {};
  // `isHesa` means "this course carries HESA-verified fields we can
  // trust as UK-national defaults" — true whether the row's own id is
  // a uk-hesa-* stub OR the row is an editorial write-up that we've
  // merged HESA data into via `enrich_editorial_from_hesa.py`.
  const isHesa = (typeof d.id === "string" && d.id.startsWith("uk-hesa-"))
    || !!hs.merged_from;
  // Never add the semester fee on top of a tuition figure that already includes required fees, or whose coverage is unknown.
  // Unresolved complete fees: add only known listed fees (a partial reference), never the legacy semester fee.
  const semesterFee = (intlFo?.includes_required_fees || indicativeTotal) ? 0
    : partialFeesNote ? (intlFo?.required_fees_usd ?? 0) : (intlFo?.required_fees_usd ?? d.mandatory_semester_fee_usd ?? 0);
  const yr = d.duration_years || 3;
  // Extras not covered by reviewed-plan evidence are listed separately, never folded into the total.
  const inheritedNote = Object.keys(inheritedExtras(c)).length ? "Partial · excludes additional costs whose provenance is not verified for this fee reference (year unknown) · not a complete degree cost" : null;
  const extraPerYear = inheritedNote ? 0 : (c.lab_studio_fees_usd || 0) + (c.equipment_software_usd || 0) + (c.books_materials_usd || 0) + (c.compulsory_travel_usd || 0);
  // A first-academic-year rate is never multiplied into a degree total.
  const fyOnly = firstYearOnly(d.cost);
  const totalDegree = fyOnly ? null : ((c.tuition_intl_annual_usd || 0) * yr) + extraPerYear * yr + semesterFee * yr;
  // Home-status total: uses domestic tuition, same extras + semester fee.
  const totalDegreeHome = !fyOnly && c.tuition_domestic_annual_usd != null
    ? ((c.tuition_domestic_annual_usd || 0) * yr) + extraPerYear * yr + semesterFee * yr
    : null;
  // Only count scholarships that actually apply to this degree's level —
  // otherwise a Masters-only £30k award inflates the "Net after scholarships"
  // figure on an undergrad page.
  const scholCredit = filterScholarshipsByLevel(
    d.scholarships || [], d.qualification, d.duration_years,
  ).reduce((s, x) => s + (x.value_usd || 0), 0);
  const netCost = totalDegree == null ? null : Math.max(0, totalDegree - scholCredit);
  const netCostHome = totalDegreeHome != null ? Math.max(0, totalDegreeHome - scholCredit) : null;

  // --- Header meta fallbacks (HESA-verified rows only carry hesa_stats,
  // so we surface those instead of leaving the top-of-page bar with
  // "null · null · null" holes). ---
  // HESA institution.city sometimes contains a street-address fragment
  // (e.g. "13-15 Parkwood Road") because the HESA CSV concatenates the
  // provider's first address line. Only accept it when it looks like a
  // real city — otherwise fall back to the university's city.
  const looksLikeCity = (s) => typeof s === "string" && s.length > 1
    && !/^\d/.test(s.trim())
    && !/\b(road|street|avenue|lane|drive|way|close|court|place|building|floor|suite|po box|park|estate)\b/i.test(s);
  const rawCity = hs.institution?.city;
  const metaCampus = d.campus || (looksLikeCity(rawCity) ? rawCity : null) || uni?.city;
  const metaIntake = d.intake || (isHesa ? "September" : null);
  const metaDept = d.department || d.subject_area;
  const metaAwarding = d.awarding_institution || hs.institution?.trading_name || hs.institution?.legal_name || uni?.name;
  // Prefer the curated real UCAS course code (letter+3digits) when we
  // have it — that unlocks a direct-apply URL. Fall back to whatever
  // application_code / HESA KIS id the row carries.
  const realUcasCode = d.ucas_course_code || null;
  const metaAppCode = realUcasCode || d.application_code || hs.ucas_code || d.ucas_prog_id;
  const modeBits = [];
  if (d.mode) modeBits.push(d.mode);
  if (hs.course_meta?.honours) modeBits.push("Honours");
  if (hs.course_meta?.sandwich_placement) modeBits.push("Sandwich");
  if (hs.course_meta?.year_abroad_available) modeBits.push("Year abroad");
  if (hs.course_meta?.distance_learning) modeBits.push("Distance learning");

  // --- Conclusion-card fallbacks. Never fabricate a percentage we don't
  // have — instead surface HESA-published proxies (typical A-level offer,
  // work-or-study rate) with a link back to Discover Uni evidence. ---
  // Country-aware Academic Fit: default to the destination country's home
  // grade system so a Melbourne BA leads with ATAR, a UK course leads with
  // A-Level, an ANU degree with ATAR, etc. Then always show the two
  // universal secondaries (A-Level + IB, where relevant) beneath so
  // international applicants aren't left guessing what "92" or "17/20" means.
  const academicFit = pickAcademicFit(uni, d.entry_requirements, hs, d.admissions);
  // New structured signals — feeds both the "Academic fit" conclusion
  // tile (collapsed to one line via primaryDisplayValue) AND the full
  // three-row AcademicFitPanel further down the page.
  const academicSignals = pickAcademicSignals(uni, d);
  const academicPrimary = primaryDisplayValue(academicSignals);
  const academicFitValue = academicPrimary.value !== "—"
    ? academicPrimary.value
    : (academicFit.primary?.value || "—");
  // The "est." pill only fires when we're rendering the fallback
  // (`pickAcademicFit`) AND the underlying entry_requirements row
  // carried `estimated: true` (i.e. our US acceptance-rate GPA tier).
  // The primary UK path (advertised/intake/uni-wide via
  // `pickAcademicSignals`) is always sourced, never estimated.
  const academicFitEstimated =
    academicPrimary.value === "—" && academicFit.primary?.estimated === true;
  const isAuditionLed = academicFit.entry_gate === "audition" || academicFit.entry_gate === "portfolio";
  const academicFitEvidence = d.entry_requirements?.a_level?.evidence
    || (isAuditionLed && d.admissions?.entry_gate_source_url
        ? { source: "Uni Apply page (audition-led)", url: d.admissions.entry_gate_source_url, confidence: "verified" }
        : null)
    || (isHesa && hs.typical_a_level ? { source: "HESA Discover Uni", date_checked: hs.phase3_enriched_at, confidence: "verified" } : null);

  // HESA's `employment_workstudy_pct` is the "in work OR further
  // study" combined figure — NOT pure employment. The pure-employment
  // figure lives at `hesa_stats.employment_breakdown.activity_pct.work`.
  // We prefer the pure-work signal for the "% in work" hero stat and
  // separately expose the combined figure below.
  const hesaWorkPct     = hs.employment_breakdown?.activity_pct?.work ?? null;
  const hesaWorkStudyPct = hs.employment_workstudy_pct ?? null;
  const employmentPct = d.outcomes?.employment_rate != null
    ? d.outcomes.employment_rate
    : (hesaWorkPct != null ? hesaWorkPct / 100 : null);
  const uniWideOutcome = d.outcomes?.scope === "university_wide" && d.outcomes?.employment_rate != null;
  const careerLabel = employmentPct != null
    ? `${Math.round(employmentPct * 100)}% in work${uniWideOutcome ? " (university-wide)" : ""}`
    : (hesaWorkStudyPct != null
        ? `${Math.round(hesaWorkStudyPct)}% in work or study`
        : "Not published");
  const careerEvidence = d.outcomes?.evidence
    || (isHesa && (hesaWorkPct != null || hesaWorkStudyPct != null) ? { source: "HESA Discover Uni · Graduate Outcomes", confidence: "verified" } : null);

  const isDaad = d.source_provenance?.source === "daad_intl_programmes";
  const isScorecard = d.source_provenance?.source === "college_scorecard_api";
  // Editorial A-level always wins over HESA-derived when both exist —
  // the HESA median can under-report elite offers because it mixes
  // Access / foreign / mature entrants at low UCAS-tariff bands.
  const editorialALevelTypical = d.entry_requirements?.a_level?.typical;
  // Prefer structured UCAS tariff (advertised standard offer) over the
  // HESA-derived "Pass"/median. "Pass" is a HESA sentinel for open-access
  // provision — meaningless as an "offer" for a prospective applicant.
  const advertisedTariff = d.entry_requirements?.ucas_tariff?.standard;
  const isMeaningfulAlevelText = editorialALevelTypical
    && String(editorialALevelTypical).trim().toLowerCase() !== "pass";

  // Derived competitiveness bucket — used ONLY when we don't have a real
  // `offer_rate` on the record. Signals: advertised UCAS tariff first,
  // then HESA-measured intake tariff. UK tariff-band heuristic mirrors
  // widely-published UCAS EOC bands (≥152 = Russell-Group-typical entry).
  //
  // For audition/portfolio-led conservatoires we bypass this entirely —
  // "Competitive" derived from HESA cohort tariff is meaningless when
  // the audition is the actual gate.
  const competitivenessBucket = (() => {
    if (isAuditionLed) {
      return {
        label: d.admissions?.entry_gate === "portfolio" ? "Portfolio-led" : "Audition-led",
        band: d.admissions?.published_ucas_tariff
          ? `${d.admissions.published_ucas_tariff}-pt floor`
          : "no tariff floor",
      };
    }
    const t = advertisedTariff ?? hs?.typical_tariff_points;
    if (t == null) return null;
    // Continuation rate boosts the bucket when signals agree.
    const cont = hs?.continuation_rate;
    let base;
    if (t >= 152) base = { label: "Highly competitive", band: "≥152 pts" };
    else if (t >= 128) base = { label: "Competitive", band: "128-151 pts" };
    else if (t >= 96) base = { label: "Moderate", band: "96-127 pts" };
    else base = { label: "Accessible", band: "<96 pts" };
    // Upgrade one tier if continuation rate ≥ 92% AND tariff is on a band edge
    if (cont != null && cont >= 0.92 && t >= 120 && t < 152) {
      base = { label: "Highly competitive", band: `${t} pts · 92%+ continuation` };
    }
    return base;
  })();

  const admissionProb = d.admissions?.offer_rate != null
    ? `${(d.admissions.offer_rate * 100).toFixed(1)}%`
    : (competitivenessBucket?.label ??
       (isDaad ? "Direct application" :
        isScorecard && uni?.scorecard_admissions?.acceptance_rate != null
          ? `${(uni.scorecard_admissions.acceptance_rate * 100).toFixed(1)}% institutional`
          : "Offer rate not published"));
  const admissionEvidence = d.admissions?.evidence
    || (isAuditionLed
        ? { source: "Uni Apply page (audition-led)",
            url: d.admissions?.entry_gate_source_url,
            confidence: "verified" }
        : null)
    || (competitivenessBucket
        ? { source: `Derived from ${advertisedTariff != null ? "advertised UCAS tariff" : "HESA median-intake tariff"}${competitivenessBucket.band ? ` (${competitivenessBucket.band})` : ""}`,
            confidence: "derived" }
        : null)
    || (isDaad ? { source: "DAAD International Programmes", url: d.source_provenance?.url, confidence: "verified" } : null)
    || (isScorecard && uni?.scorecard_admissions?.acceptance_rate != null
        ? { source: "US Dept of Education — College Scorecard", url: d.source_provenance?.url, confidence: "verified" }
        : null);

  // "Known tuition" means we've stored a concrete number — including $0,
  // which is legitimate for German public universities. Guard the labels
  // this way so `totalDegree === 0` still renders as "$0" rather than "—".
  const knownTuition = c.tuition_intl_annual_usd != null || c.tuition_domestic_annual_usd != null;
  const partialTag = partialFeesNote || inheritedNote ? " (partial)" : "";
  const totalCostLabel = fyOnly ? FIRST_YEAR_TOTAL_LABEL : (totalDegree > 0 || knownTuition) ? format(totalDegree) + partialTag : (isHesa ? "See course page" : "—");
  const netCostLabel   = fyOnly ? FIRST_YEAR_TOTAL_LABEL : (totalDegree > 0 || knownTuition) ? format(netCost) + partialTag : (isHesa ? "See course page" : "—");
  const totalCostHomeLabel = totalDegreeHome != null ? format(totalDegreeHome) + partialTag : null;
  const netCostHomeLabel   = netCostHome != null ? format(netCostHome) + partialTag : null;

  const intlViable = d.international?.visa_eligible === true ? "Eligible"
    : d.international?.visa_eligible === false ? "Restricted"
    : (isHesa || uni?.country_code === "GB") ? "UK Graduate Route · 2y PSW"
    : "See course page";
  const intlViableEvidence = d.international?.evidence
    || ((isHesa || uni?.country_code === "GB") ? { source: "GOV.UK · Graduate visa route", url: "https://www.gov.uk/graduate-visa", confidence: "verified" } : null);

  // Section 7 (Placements) is hidden entirely when there's no data at
  // all — for HESA / DAAD / Scorecard rows this is common because the
  // upstream feed doesn't publish per-course placement detail. Rendering
  // a wall of "Not published" adds no value and makes the page feel
  // broken (see 2026-02-28 UX pass). We check for ANY signal:
  //   * an editorial `placement` block
  //   * an editorial `study_abroad` block with the course
  //   * HESA `course_meta.sandwich_placement` = true
  //   * HESA `course_meta.year_abroad_available` = true
  const _placementMeta = (d.hesa_stats || {}).course_meta || {};
  const placementHasData = Boolean(
    d.placement
    || d.study_abroad?.available
    || _placementMeta.sandwich_placement === true
    || _placementMeta.year_abroad_available === true
  );

  // Section 10 (Scholarships) — hide when we have literally nothing to
  // show. Same principle as Placements above. Note US Scorecard rows
  // ALSO render the `UsFinancialAid` widget as a fallback, so we keep
  // the section for those even when there's no explicit scholarship
  // list. We filter the two source lists by academic level here so that
  // when a Masters-only fallback list is the only thing available on an
  // undergrad course, the section stays hidden rather than showing an
  // irrelevant wall of postgrad awards.
  const _ugpgFilteredDegreeSchols = filterScholarshipsByLevel(
    d.scholarships || [], d.qualification, d.duration_years,
  );
  const _ugpgFilteredUniSchols = filterScholarshipsByLevel(
    uni?.scholarships_available || [], d.qualification, d.duration_years,
  );
  const scholarshipHasData = Boolean(
    _ugpgFilteredDegreeSchols.length
    || _ugpgFilteredUniSchols.length
    || isScorecard  // UsFinancialAid takes over the empty state
  );

  // Section 11 (International) — hide when there's no per-course
  // international policy at all. Country-level PSW info still surfaces
  // in the hero chip ("International viability: UK Graduate Route · 2y
  // PSW") so families aren't left in the dark; the section itself
  // only carries per-course policy overrides.
  const internationalHasData = Boolean(
    d.international && Object.keys(d.international).length > 0
  );

  // Section 8 (Graduate Outcomes) — hide when every rendered widget
  // would come up empty. Signals:
  //   * course-level HESA/KIS employment or salary figures
  //   * editorial outcomes fields (employment_rate, salaries, top
  //     employers, destinations, international employment)
  //   * subject-wide fallbacks (DAAD baseline for DE, Scorecard
  //     baseline for US, LEO sector chart if UK)
  // If NONE of these fire, the section renders as a wall of "—" with
  // no interpretive value — same pattern as Placements above.
  const _outcomes = d.outcomes || {};
  const outcomesHasData = Boolean(
    _outcomes.employment_rate != null
    || _outcomes.highly_skilled_employment_pct != null
    || _outcomes.postgrad_study_pct != null
    || _outcomes.median_starting_salary_usd != null
    || _outcomes.salary_after_3y_usd != null
    || _outcomes.international_employment_rate != null
    || (_outcomes.top_employers?.length ?? 0) > 0
    || (_outcomes.destinations?.top_employers?.length ?? 0) > 0
    || hs.employment_workstudy_pct != null
    || hs.salary_median_gbp != null
    || hs.leo3_median_gbp != null
    || hs.leo5_median_gbp != null
    || hs.employment_breakdown
    || (hs.top_graduate_jobs?.length ?? 0) > 0
    || hs.leo_sec
    // Subject-wide baselines that self-render only for DE / US rows.
    || isDaad
    || isScorecard
  );

  const hiddenNavSections = new Set([
    ...(placementHasData ? [] : ["placement"]),
    ...(scholarshipHasData ? [] : ["scholarships"]),
    ...(internationalHasData ? [] : ["international"]),
    ...(outcomesHasData ? [] : ["outcomes"]),
  ]);

  // Feb 2026 refactor — one object bundles every derived value the six
  // extracted cluster modules read. Destructured at the top of each
  // cluster file so we avoid a giant prop signature per component.
  const ctx = {
    d, uni, hs, c, ai,
    isHesa, isDaad, isScorecard,
    academicSignals,
    admissionEvidence,
    placementHasData, outcomesHasData, scholarshipHasData, internationalHasData,
    totalDegree, knownTuition, fyOnly,
    metaAwarding,
    employerLifts,
    format, currency,
  };

  return (
    <DegreePageProvider degreeId={d.id} universityId={d.university_id}>
    <DegreeSEO degree={d} uni={uni} aiSummary={ai?.interpretation?.summary} />
    <DegreeBreadcrumb uni={uni} degree={d} />
    <div
      className="max-w-6xl mx-auto px-6 lg:px-10 py-10"
      data-text-size={textSize}
      data-testid="degree-page-root"
    >
      <Link to={uni ? `/uni/${uni.id}` : "/explore"} className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" /> {uni ? uni.name : "Back"}
      </Link>

      {d.not_offered && (
        <div role="status" data-testid="degree-not-offered-banner"
          className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong className="font-semibold">This course isn't currently offered.</strong>{" "}
          We've checked and {uni?.name || "this university"} doesn't list it, so it no longer appears in searches.
          It stays here so saved shortlists still work.
        </div>
      )}
      <VerifyMoreNudge degreeId={d.id} universityId={d.university_id} />
      <ClaimDiscoveryCTA universityId={d.university_id} universityName={uni?.name} />

      {/* Feb 19, 2026 — Comfortable / Compact segmented toggle. Sits
          above the course title so parents see it immediately when
          they land on the page. Defaults to "Comfortable" (larger)
          for first-time visitors, remembered via localStorage. */}
      <div className="mt-4 flex justify-end" data-testid="text-size-toggle-wrap">
        <TextSizeToggle value={textSize} onChange={setTextSize} />
      </div>

      <div className="mt-3 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase" data-testid="degree-meta-line">
            {d.qualification}
            {metaCampus ? <> · {metaCampus}</> : null}
            {d.duration_years ? <> · {d.duration_years} years</> : null}
            {metaIntake ? <> · {metaIntake}</> : null}
            {modeBits.length ? <> · {modeBits.join(" · ")}</> : null}
          </div>
          <h1 data-testid="degree-title" className="font-editorial text-4xl sm:text-5xl mt-2 leading-tight">{d.official_title}</h1>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <div className="text-sm text-stone-600" data-testid="degree-department-line">
              {metaDept ? <>{metaDept} · </> : null}{metaAwarding}
            </div>
            <FoundationYearPill show={d.foundation_year === true} size="md" />
            <VerifiedCoverageChip degreeId={d.id} />
            <StudentLovedBadge
              stats={satisfaction}
              size="lg"
              testid={`student-loved-degree-${d.id}`}
            />
            {d.last_uni_update_at && (
              <VerifiedByUniBadge
                lastUpdatedAt={d.last_uni_update_at}
                updatedBy={d.last_uni_update_by}
                compact
              />
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="label-uppercase !text-[10px]">Application code</div>
          <div className="font-mono" data-testid="degree-application-code">
            {realUcasCode ? (
              <>
                <a
                /* Real UCAS applicant code + institution code paired
                   — the UCAS Track/Apply flow keys on BOTH values.
                   "L0V0" alone means "PPE somewhere"; "L0V0 · O33"
                   uniquely identifies "PPE at Oxford". Search URL
                   carries both tokens so UCAS returns the exact
                   course card in one hop. */
                href={`https://digital.ucas.com/search?query=${encodeURIComponent(
                  realUcasCode + (uni?.ucas_institution_code ? " " + uni.ucas_institution_code : "") + " " + (uni?.name || "")
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4C1D95] hover:underline"
                data-testid="degree-ucas-link"
              >
                <span className="font-semibold">{realUcasCode}</span>
                {uni?.ucas_institution_code && (
                  <span className="text-stone-500 font-normal">
                    {" · "}{uni.ucas_institution_code}
                  </span>
                )}
                {d.ucas_course_code_variant && (
                  <span className="text-stone-500 font-normal ml-1">
                    · {d.ucas_course_code_variant}
                  </span>
                )}
              </a>
              <CopyPairedUcasButton
                courseCode={realUcasCode}
                institutionCode={uni?.ucas_institution_code}
              />
              </>
            ) : metaAppCode ? (
              isHesa ? (
                <a
                  /* UCAS search always resolves — the coursedisplay
                     path 404s for HESA-KIS codes and compound tokens
                     like "A15-C69" or "UCAS/CommonApp/Direct". Send
                     users to the unified search pre-filled with
                     university name so they land on the real course
                     page in one click. */
                  href={`https://digital.ucas.com/search?query=${encodeURIComponent(
                    [uni?.name, d.official_title].filter(Boolean).join(" ").trim()
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-dotted decoration-stone-400 hover:decoration-[#4C1D95] hover:text-[#4C1D95] transition"
                  data-testid="degree-ucas-link"
                >
                  {metaAppCode}
                </a>
              ) : (
                metaAppCode
              )
            ) : (
              isHesa ? (
                <a
                  href={`https://digital.ucas.com/search?query=${encodeURIComponent(
                    [uni?.name, d.official_title].filter(Boolean).join(" ").trim()
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#4C1D95] underline decoration-dotted"
                  data-testid="degree-ucas-link"
                >
                  See UCAS
                </a>
              ) : "—"
            )}
          </div>
          {/* Live-verified against UCAS' Algolia index — a green tick
              tells families they can trust this exact code to open the
              right course card in UCAS Track/Apply, so no risk of a
              404 or a wrong flavour. */}
          {realUcasCode && d.ucas_course_code_live_verified && (
            <div
              className="mt-1.5 text-[11px] font-underground-sc tracking-wider text-emerald-700 flex items-center gap-1 justify-end"
              data-testid="degree-ucas-verified-badge"
              title="This course code was confirmed live against UCAS' own search index"
            >
              <span aria-hidden="true">✓</span> UCAS verified
            </div>
          )}
          {/* UCAS Institution code + 2027-entry badge — makes it
              obvious to families which cycle they're looking at
              and what to quote when they ring the admissions office. */}
          {uni?.ucas_institution_code && (
            <div className="mt-1.5 text-[11px] font-underground-sc tracking-wider text-stone-500" data-testid="degree-ucas-institution-code">
              UCAS Institution · <span className="text-stone-800">{uni.ucas_institution_code}</span>
            </div>
          )}
          {d.cycle_label && (
            <div className="mt-1 text-[11px] font-underground-sc tracking-wider text-[#4C1D95]" data-testid="degree-cycle-label">
              For {d.cycle_label}
            </div>
          )}
          {/* Clearing summary chip — surfaces whether this uni typically
              runs Clearing (mid-July → mid-October) and links to their
              own admissions hotline page. We deliberately don't quote
              real-time vacancy state — UCAS + the uni own that data.

              CAPPED-SUBJECT OVERRIDE: Medicine (MBBS), Dentistry (BDS),
              Veterinary Medicine and Pharmacy face home-student quotas
              set by the DfE/HEE + regulators (GMC, GDC, RCVS, GPhC), so
              firm+insurance offers fill the class every year and
              Clearing spots surface only in rare regulator roundups.
              Showing plain "Clearing available" on those courses is
              misleading, so we flip the pill to an amber "Clearing
              rarely available" with an explanatory tooltip. */}
          {(() => {
            const rawTitle = (d.official_title || d.name || "").toLowerCase();
            const rawId = (d.id || "").toLowerCase();
            const CAPPED = [
              // Medicine — matches "Medicine", "MBBS", "MB BChir", "MB ChB".
              /\bmedicine\b/, /\bmbbs\b/, /\bmb\s*bchir\b/, /\bmb\s*chb\b/,
              // Dentistry.
              /\bdentistry\b/, /\bbds\b/,
              // Veterinary Medicine.
              /\bveterinary\b/, /\bvet\s*med\b/, /\bbvetmed\b/, /\bvetmb\b/,
              // Pharmacy (regulated by GPhC).
              /\bpharmacy\b/, /\bmpharm\b/,
            ];
            const isCapped = CAPPED.some((rx) =>
              rx.test(rawTitle) || rx.test(rawId)
            );
            if (uni?.clearing?.typically_runs_clearing === true) {
              if (isCapped) {
                return (
                  <a
                    href="https://www.ucas.com/undergraduate/after-you-apply/results/undergraduate-clearing-what-you-need-know"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="degree-clearing-chip-capped"
                    className="mt-2 mr-2 inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-amber-50 px-3 py-1 text-[11px] font-underground-sc tracking-wider text-amber-900 hover:bg-amber-100"
                    title="Medicine, Dentistry, Vet Med and Pharmacy face home-student number caps set by regulators (GMC / GDC / RCVS / GPhC). Firm + insurance offers fill the class every year, so Clearing rarely surfaces spots. Occasional openings appear via UCAS's Clearing tracker."
                  >
                    Clearing rarely available
                  </a>
                );
              }
              return (
                <a
                  href={uni.clearing.hotline_url || "https://www.ucas.com/undergraduate/results-confirmation-and-clearing"}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="degree-clearing-chip"
                  className="mt-2 mr-2 inline-flex items-center gap-1.5 rounded-full border border-amber-500 bg-amber-50 px-3 py-1 text-[11px] font-underground-sc tracking-wider text-amber-800 hover:bg-amber-100"
                  title={uni.clearing.note || "This uni typically runs Clearing each August. Tap for their hotline."}
                >
                  Clearing available
                </a>
              );
            }
            if (uni?.clearing?.typically_runs_clearing === false) {
              return (
                <div
                  data-testid="degree-no-clearing-chip"
                  className="mt-2 mr-2 inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-stone-50 px-3 py-1 text-[11px] font-underground-sc tracking-wider text-stone-600"
                  title={uni.clearing.note || "This uni does not usually run Clearing."}
                >
                  No Clearing at this uni
                </div>
              );
            }
            return null;
          })()}
          {d.currently_recruiting === false && (
            <Badge variant="destructive" className="mt-2">Not currently recruiting</Badge>
          )}
          <Button
            data-testid="add-degree-shortlist-btn"
            onClick={toggleShortlist}
            size="sm"
            title={saved ? "Click to remove from your shortlist" : "Add this degree to your shortlist"}
            className={`mt-3 ${saved ? "bg-emerald-700 hover:bg-emerald-800" : "bg-[#4C1D95] hover:bg-[#3B0764]"} text-white`}>
            {saved ? <><Check className="h-4 w-4 mr-1.5" /> Shortlisted · click to remove</>
                   : <><Bookmark className="h-4 w-4 mr-1.5" /> Shortlist this degree</>}
          </Button>
          <Link
            to={`/compare?d1=${d.id}`}
            data-testid="compare-this-degree-btn"
            className="mt-2 ml-2 inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white text-stone-800 hover:bg-stone-100 text-sm px-3 py-1.5">
            <GitCompare className="h-4 w-4" /> Compare with…
          </Link>
          {/* Apply readiness — refreshes as soon as the shortlist
              toggle flips so the "how do I move on?" question is
              answered inline, not on some other page. `saved` is
              boolean; refetching on its change gives the counter
              real-time feedback. */}
          <ApplyReadinessCard
            key={saved ? "on" : "off"}
            compact
            className="mt-3 max-w-xl"
            testId="degree-detail-readiness"
          />
        </div>
      </div>

      {/* Course-flavour picker — sibling variants at this uni */}
      <VariantPicker degreeId={d.id} />

      {/* Audition-led entry chip — surfaces the real entry gate so students
          don't misread HESA-derived tariff figures as the offer bar. */}
      {isAuditionLed && (
        <div
          data-testid="audition-led-chip"
          className="mt-4 inline-flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 max-w-2xl"
        >
          <span className="font-underground-sc tracking-wider text-[10px] mt-0.5">
            {d.admissions?.entry_gate === "portfolio" ? "Portfolio-led" : "Audition-led"}
          </span>
          <span className="opacity-90">
            {d.admissions?.entry_gate_notes || (
              "Entry to this course is decided at audition / portfolio review. " +
              "Tariff figures on this page are a floor, not the offer bar."
            )}
            {d.admissions?.entry_gate_source_url && (
              <>
                {" "}
                <a
                  href={d.admissions.entry_gate_source_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline decoration-amber-500/60 underline-offset-2 hover:text-amber-950"
                >
                  See uni's Apply page
                </a>.
              </>
            )}
          </span>
        </div>
      )}

      {/* Feb 2026 · Limited-data banner — friendly heads-up when
          the course record is a stub (no admissions, no HESA, no
          cost). Keeps sparse pages from reading like broken data. */}
      <LimitedDataBanner degree={d} uni={uni} />

      {/* The Professor · At a glance — warm, human-readable overview at the top */}
      <AIInterpretationSummary
        interpretation={ai.interpretation}
        loading={ai.loading}
        generating={ai.generating}
        error={ai.error}
        reload={ai.reload}
        degreeId={d.id}
        inheritedFrom={ai.inheritedFrom}
      />

      {/* Data Honesty Report Card — trust anchor visible before the family
          reads any per-section number. Shows exactly which sections carry
          a primary source citation vs which are honestly blank. */}
      <DataHonestyBadges degree={d} />

      {/* Note: the 90-second campus tour moved to the parent
          University page's Life tab (right column) — one canonical
          home for it, avoids duplicating the same iframe across
          every degree that inherits the same university. */}

      {/* 6 CONCLUSIONS + optional cost-gap widget (auto-triggers for
          Germany / France public and any degree < 30% of UK+US median). */}
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        {[
          ["Admission probability", admissionProb, admissionEvidence, null,
            "How competitive this course is. When we have the university's real offer rate we show it as a percentage. Otherwise we bucket the course by advertised UCAS tariff — ≥152 pts = Highly competitive (Russell-Group-typical), 128-151 = Competitive, 96-127 = Moderate, <96 = Accessible. Continuation rate ≥92% can bump a course up a tier when signals agree. This is a proxy, NOT a personal prediction."],
          ["Academic fit", academicFitValue || "—", academicFitEvidence, null,
            "The grade bar this course asks for. When the university publishes a UCAS tariff standard offer we show that (with the contextual offer alongside when available) — this is what you'll need to hit to be a competitive applicant. Otherwise we show the typical A-level / IB / grade band of students who were admitted last cycle. Descriptive, not a personal prediction."],
          ["Career alignment", careerLabel, careerEvidence, null,
            "The proportion of graduates from this course in professional (graduate-level) work 15 months after finishing, per HESA Graduate Outcomes. Higher means the degree tends to lead directly to skilled work rather than to further study or informal jobs."],
          ["Total degree cost", indicativeTotal && totalDegree > 0 ? `≈ ${totalCostLabel} (indicative)` : estTotalNote && totalDegree > 0 ? `≈ ${totalCostLabel} (estimated)` : totalCostLabel, courseTuitionBlocked || indicativeTotal || estTotalNote || partialFeesNote ? null : c.evidence, totalCostHomeLabel && totalCostHomeLabel !== totalCostLabel ? `Home ${totalCostHomeLabel}` : null,
            "International tuition × programme length + any mandatory lab/studio/equipment fees. Excludes living costs (accommodation, food, transport) — see the Cost tab for the full breakdown. UK Home fee is the regulated £9,535/year cap for 2025/26."],
          ["Net after scholarships", indicativeTotal && totalDegree > 0 ? `≈ ${netCostLabel} (indicative)` : estTotalNote && totalDegree > 0 ? `≈ ${netCostLabel} (estimated)` : netCostLabel, null, netCostHomeLabel && netCostHomeLabel !== netCostLabel ? `Home ${netCostHomeLabel}` : null,
            "Total degree cost minus any scholarships listed on this course. Does NOT include country-of-origin bursaries or external funding you might independently secure — those come off the top on your personal shortlist page."],
          ["International viability", intlViable, intlViableEvidence, null,
            "Whether this course qualifies for the country's post-study work visa (e.g. UK Graduate Route, US OPT/STEM-OPT, Netherlands Zoekjaar). Based on official government policy for the course level and campus location."],
        ].map(([k, v, ev, sub, tip]) => (
          <Card key={k} data-testid={`conclusion-${k.replace(/ /g, "-").toLowerCase()}`} className="p-5 border-stone-200 bg-white">
            <div className="flex items-center gap-1.5">
              <div className="label-uppercase !text-[10px]">{k}</div>
              {tip && (
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={`Explain ${k}`}
                        data-testid={`tooltip-${k.replace(/ /g, "-").toLowerCase()}`}
                        className="text-stone-400 hover:text-[#4C1D95] transition"
                      >
                        <Info className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-xs bg-[#002147] text-white text-[11px] leading-relaxed p-3"
                    >
                      {tip}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="font-chip text-2xl font-semibold mt-2 flex items-baseline gap-2">
              <span>{v}</span>
              {k === "Academic fit" && academicFitEstimated && (
                <span
                  data-testid="academic-fit-estimated-pill"
                  title="Estimated from institutional acceptance rate — not a published cutoff."
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold tracking-wider uppercase bg-amber-100 text-amber-900 border border-amber-300"
                >
                  est.
                </span>
              )}
            </div>
            {k === "Academic fit" && (academicPrimary.system || academicFit.primary) && (
              <div
                className="mt-0.5 text-[10px] font-underground-sc tracking-wider text-stone-500"
                data-testid="academic-fit-source-label"
              >
                {academicPrimary.system || academicFit.primary?.system}
              </div>
            )}
            {k === "Academic fit" && (
              <Link
                to="/grade-calculator"
                data-testid="academic-fit-grade-calc-link"
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline underline-offset-2"
              >
                Convert my grades →
              </Link>
            )}
            {sub && (
              <div
                className="font-mono text-sm text-stone-500 mt-1"
                data-testid={`conclusion-${k.replace(/ /g, "-").toLowerCase()}-sub`}
              >
                {sub}
              </div>
            )}
            {partialFeesNote && totalDegree > 0 && (k === "Total degree cost" || k === "Net after scholarships") && (
              <div className="mt-1 text-[11px] text-amber-800" data-testid={`conclusion-${k.replace(/ /g, "-").toLowerCase()}-partial-note`}>
                {partialFeesNote}
              </div>
            )}
            {inheritedNote && totalDegree > 0 && (k === "Total degree cost" || k === "Net after scholarships") && (
              <div className="mt-1 text-[11px] text-amber-800" data-testid={`conclusion-${k.replace(/ /g, "-").toLowerCase()}-inherited-note`}>
                {inheritedNote}
              </div>
            )}
            {estTotalNote && totalDegree > 0 && (k === "Total degree cost" || k === "Net after scholarships") && (
              <div className="mt-1 text-[11px] text-amber-800" data-testid={`conclusion-${k.replace(/ /g, "-").toLowerCase()}-estimate-note`}>
                {estTotalNote}
              </div>
            )}
            <Ev ev={ev} />
          </Card>
        ))}
        <CostGapCard degreeId={d.id} />
        <TuitionBasisNote intl={intlFo} blocked={courseTuitionBlocked} />
        <YourFeeHereTile
          country={uni?.country}
          intlFeeUsd={c?.tuition_intl_annual_usd}
          homeFeeUsd={c?.tuition_domestic_annual_usd}
          indicative={indicativeTotal || intlFromUniFallback}
          note={feeTileNote}
          homeIndicative={homeTileIndicative}
          homeNote={homeTileNote}
          testId="degree-fee-status-tile"
        />
      </div>

      {/* Entry-profile mini-strip: aligned with Admission-Probability card */}
      <div className="mt-2 md:grid md:grid-cols-3 md:gap-4">
        <div className="md:col-span-1">
          <EntryProfileMini degree={d} />
        </div>
      </div>

      {/* Feb 2026 dedupe: DiscoverUniHeroStrip / LEOSalaryStrip /
          SalaryProjectionChart used to render here at the top, then
          again inside §8 Outcomes and §3 Admissions. Kept only in
          their true cluster homes (Cluster 4 · Return on investment). */}

      {/* Government-verified visa sponsor chip. Only renders when the
          uni sits in the sponsor register data — falls back silently
          otherwise so we never claim a status we can't prove. */}
      {uni?.visa_sponsor?.authority && (
        <div className="mt-4">
          <VisaSponsorVerifiedChip
            sponsor={uni.visa_sponsor}
            size="md"
            testid="visa-sponsor-degree-detail"
          />
        </div>
      )}

      {/* Data-quality honesty band — hidden when record_evidence.confidence
          is "verified" with a real URL; visible everywhere else so
          applicants double-check numbers before applying. */}
      <EvidenceBanner
        verified={d.content_verified || d.source_tier === "verified"}
        universityName={d.university?.name}
        evidence={d.record_evidence || d.source_provenance}
      />

      {/* Sticky section navigator — anchor menu that highlights the current
          section as the user scrolls. Sits below the hero strip so the big
          summary + conclusions stay in view above the fold. */}
      {/* --- Feb 2026 course-page redesign additions ---
          Four self-contained modules that light up as data lands.
          Each one hides gracefully when its source data is absent
          so a directory-tier row still renders cleanly today. */}

      {/* 1. 3 punchy differentiators — pulled from `degree.differentiators`
             (editorial-tier field, seeded by ops). Hidden entirely on
             rows that don't yet have three. */}
      <DifferentiatorsGrid items={d.differentiators} />

      {/* Campus map — moved to the university page (Feb 2026). Same
          content was rendering on every course page for a given uni,
          so we link out to the uni-level campus tour instead. */}
      {uni?.id && (uni?.city || uni?.name) && (
        <div className="mt-6">
          <a
            href={`/uni/${uni.id}?tab=life`}
            data-testid="campus-map-link-out"
            className="inline-flex items-center gap-2 text-sm font-medium text-stone-700 hover:text-stone-900 underline underline-offset-4 decoration-stone-400 hover:decoration-stone-900"
          >
            <Landmark className="w-4 h-4" />
            See the {uni.name} campus map & Life tab →
          </a>
        </div>
      )}

      {/* Feb 2026 dedupe: StudentVoiceQuote used to render here at
          the top of the page. Same data is surfaced in Cluster 5 ·
          Will you enjoy it, alongside the NSS survey + Satisfaction
          card so all "voice" content lives together. */}

      {/* 2b. "What you'll study" narrative card — per
             design_guidelines §2. Uses seeded `professor_blurb` only
             (no fallback to the top-of-page AI summary, which caused a
             duplicate paragraph on every course); when neither
             editorial copy nor curriculum modules exist, the card
             hides itself instead of showing a placeholder. */}
      <WhatYouStudyCard degree={d} />

      {/* 2c. Key faculty — hand-seeded 2-4 academics per pilot
             degree (Bath Politics, Cardiff Journalism). Silent
             when the array is empty because a fabricated professor
             is worse than no professor. */}
      <KeyFacultyCard faculty={d.key_faculty} />

      {/* Feb 2026 dedupe: EmployerProofStrip used to render here at
          the top. Now lives inside Cluster 4 · Return on investment
          alongside LEO salaries, sector chart and top-graduate-jobs. */}

      <SectionNav
        hasWarnings={(d.warnings?.length || 0) > 0}
        hiddenSections={hiddenNavSections}
      />

      <FranceEssentials degree={d} uni={uni} />

      {/* WARNINGS + STRENGTHS */}
      {(d.warnings?.length || 0) > 0 && (
        <section
          id="warnings"
          data-testid="section-warnings"
          className="scroll-mt-28 mt-10"
        >
          <div className="text-[11px] font-underground-sc tracking-[0.2em] text-red-700/80">
            Read this first
          </div>
          <h2 className="font-editorial text-3xl sm:text-4xl leading-tight text-red-900 mt-1">
            Honest warnings
          </h2>
          <div className="mt-5 space-y-2">
            {d.warnings.map((w, i) => (
              <div key={i} data-testid={`warning-${i}`} className={`border-l-4 rounded-md p-3 text-sm ${
                w.severity === "warning" ? "bg-red-50 border-red-700" : "bg-stone-50 border-stone-500"
              }`}>
                <div className="flex gap-2">
                  <AlertTriangle className={`h-4 w-4 mt-0.5 ${w.severity === "warning" ? "text-red-700" : "text-stone-600"} shrink-0`} />
                  <div>
                    <span className="label-uppercase !text-[9px]">{w.code}</span>
                    <div className="mt-0.5">{w.text}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Feb 2026 course-page restructure — 5 human-centric clusters
          plus a Trust footer. CSS `order` re-shuffles the flex
          children so each Section paints in its cluster's slot
          without any physical JSX reordering. Cluster headers are
          accordion triggers (open/close via ClusterProvider). */}
      <ClusterProvider>
      <div className="flex flex-col">

      <ClusterHeader
        cluster={1}
        id="cluster-essential"
        eyebrow="What is this course?"
        title="Essential information"
        subtitle="What you'll study, how it's taught, and who accredits it."
        topicsFilled={1 + (d.curriculum ? 1 : 0) + (d.teaching ? 1 : 0) + (((d.accreditation||[]).length + (d.hesa_stats?.accreditations||[]).length) > 0 ? 1 : 0)}
        topicsTotal={4}
      />
      <ClusterHeader
        cluster={2}
        id="cluster-applying"
        eyebrow="Can I get in, and how?"
        title="Applying & getting in"
        subtitle="Entry requirements, admissions competitiveness, application timeline and international-student suitability."
        topicsFilled={1 + (d.admissions ? 1 : 0) + (internationalHasData ? 1 : 0) + (d.application ? 1 : 0)}
        topicsTotal={4}
      />
      <ClusterHeader
        cluster={3}
        id="cluster-costs"
        eyebrow="What will it cost me?"
        title="Costs & affordability"
        subtitle="Tuition, additional fees, city living costs and every scholarship you may qualify for."
        topicsFilled={1 + (scholarshipHasData ? 1 : 0)}
        topicsTotal={2}
      />
      <ClusterHeader
        cluster={4}
        id="cluster-roi"
        eyebrow="What do I get out of it?"
        title="Return on investment"
        subtitle="Graduate salaries, top employers, placement experience and career progression."
        topicsFilled={(placementHasData ? 1 : 0) + (outcomesHasData ? 1 : 0)}
        topicsTotal={2}
      />
      <ClusterHeader
        cluster={5}
        id="cluster-enjoy"
        eyebrow="What's life like here?"
        title="Will you enjoy it?"
        subtitle="Campus life, satisfaction scores and student voice — the character of studying here."
        topicsFilled={1 + 1 + 1}
        topicsTotal={3}
      />
      <ClusterHeader
        cluster={6}
        id="cluster-trust"
        eyebrow="Sources & trust"
        title="FAQ & data provenance"
        subtitle="Every number on this page carries a source, date checked and confidence label."
        topicsFilled={((d.hesa_stats?.faq_ai_generated?.qa?.length || 0) > 0 ? 1 : 0) + 1}
        topicsTotal={2}
      />

      {/* Cluster bodies — extracted to /degree-clusters/* in the Feb 2026
          refactor. CSS `order` on the shared flex container (className
          "flex flex-col") pins each ClusterMember to its correct visual
          slot regardless of source order, so we list them 1-6 for
          readability. Each receives the same `ctx` object. */}
      <Cluster1Essential ctx={ctx} />
      <Cluster2Applying  ctx={ctx} />
      <Cluster3Costs     ctx={ctx} />
      <Cluster4ROI       ctx={ctx} />
      <Cluster5Enjoy     ctx={ctx} />
      <Cluster6Sources   ctx={ctx} />

      </div>
      </ClusterProvider>
    </div>
    </DegreePageProvider>
  );
}

